// Small GET helper for the Phase 4C screens. Unlike the older per-module helpers it never throws for HTTP errors and never
// collapses every failure into `undefined`: the caller gets { ok, status, data } and decides (401/403 are still handled centrally
// by apiResponseGuard, which observes window.fetch).
import config from "../../config.json";
import { normalizeApiError } from "../errors/normalizeApiError";

const BASE_AUTH_API = config.BASE_AUTH_API;

// Query string from a plain object: undefined / null / "" are skipped, arrays become one repeated key each.
export function buildQuery(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) {
      value.filter((item) => item !== undefined && item !== null && item !== "").forEach((item) => query.append(key, String(item)));
      return;
    }
    query.append(key, String(value));
  });
  const text = query.toString();
  return text ? `?${text}` : "";
}

// Failed answers also carry the normalized error; successful ones are returned unchanged.
const withApiError = (result) =>
  result.ok ? result : { ...result, apiError: normalizeApiError({ status: result.status, data: result.data, aborted: result.aborted }) };

// DELETE with a JSON body; same answer shape as apiGet.
export async function apiDelete(path, body, { token, signal } = {}) {
  try {
    const response = await fetch(`${BASE_AUTH_API}${path}`, {
      method: "DELETE",
      headers: { Accept: "application/json", "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(body),
      signal,
    });
    const data = await response.json().catch(() => null);
    return withApiError({ ok: response.ok && !(data && data.success === false), status: response.status, data });
  } catch (error) {
    if (error && error.name === "AbortError") return withApiError({ ok: false, aborted: true, status: 0, data: null });
    return withApiError({ ok: false, status: 0, data: null, error: error && error.message ? error.message : "Network error" });
  }
}

// as: "json" (default) or "blob". Resolves to { ok, status, data, headers, aborted?, error?, apiError? }.
// `apiError` (failures only) is the normalized error of utils/errors/normalizeApiError: pass it to errorText() or reportApiError().
export async function apiGet(path, params, { token, signal, as = "json" } = {}) {
  try {
    const response = await fetch(`${BASE_AUTH_API}${path}${buildQuery(params)}`, {
      method: "GET",
      headers: { Accept: as === "blob" ? "*/*" : "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      signal,
    });
    let data = null;
    if (as === "blob") data = response.ok ? await response.blob() : await response.json().catch(() => null);
    else data = await response.json().catch(() => null);
    return withApiError({ ok: response.ok && !(data && data.success === false), status: response.status, data, headers: response.headers });
  } catch (error) {
    if (error && error.name === "AbortError") return withApiError({ ok: false, aborted: true, status: 0, data: null });
    return withApiError({ ok: false, status: 0, data: null, error: error && error.message ? error.message : "Network error" });
  }
}
